import React, { useEffect, useState } from "react";
import {
  X,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * PdfViewerModal — single-piece PDF previewer.
 *
 * Displays exactly ONE attachment. No carousel, no arrows, no thumbnail strip,
 * no keyboard navigation. To view another piece, close and click another one.
 *
 * Props:
 *  - dossierId: string
 *  - attachment: { filename, normalized_filename, type_piece, is_bdc, size, validated? }
 *  - index: number — global position in dossier.attachments (drives the by-index endpoint)
 *  - onClose: () => void
 *  - onValidationToggle: (updatedAttachmentPatch) => void
 *      called with `{ index, validated, validated_count, total, all_validated }`
 *      so the caller can patch only this one item in its state.
 */
export default function PdfViewerModal({ dossierId, attachment, index, onClose, onValidationToggle }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [validated, setValidated] = useState(!!attachment?.validated);

  // Load PDF blob for THIS piece only — never reloads for a "next" piece.
  useEffect(() => {
    if (!attachment) return;
    const controller = new AbortController();
    let createdUrl = null;
    setLoading(true);
    setError(null);
    setBlobUrl(null);
    (async () => {
      try {
        const r = await api.get(
          `/dossiers/${dossierId}/attachment-by-index/${index}?inline=1`,
          { responseType: "blob", signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        createdUrl = url;
        setBlobUrl(url);
        setLoading(false);
      } catch (e) {
        if (controller.signal.aborted || e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
        let msg = "Impossible de charger le PDF";
        try {
          const blob = e?.response?.data;
          if (blob && typeof blob.text === "function") {
            const text = await blob.text();
            try {
              const json = JSON.parse(text);
              msg = json.detail || msg;
            } catch {
              if (text) msg = text.slice(0, 200);
            }
          }
        } catch {
          /* swallow */
        }
        if (e?.response?.status === 404) msg = "Fichier introuvable côté serveur.";
        setError(msg);
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
      if (createdUrl) {
        setTimeout(() => URL.revokeObjectURL(createdUrl), 30000);
      }
    };
  }, [dossierId, index, attachment]);

  // Close on Escape only — no arrow nav.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const downloadCurrent = async () => {
    const r = await api.get(
      `/dossiers/${dossierId}/attachment-by-index/${index}?normalized=1`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.normalized_filename || attachment.filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank");
  };

  const toggleValidation = async () => {
    setToggling(true);
    try {
      const { data } = await api.post(
        `/dossiers/${dossierId}/attachment-by-index/${index}/validate`
      );
      const nowValidated = !validated;
      setValidated(nowValidated);
      onValidationToggle?.({
        index,
        validated: nowValidated,
        validated_count: data.validated_count,
        total: data.total,
        all_validated: data.all_validated,
      });
      toast.success(
        nowValidated ? `✓ Pièce validée (${data.validated_count}/${data.total})` : "Validation retirée",
        { duration: 1500 }
      );
    } catch (e) {
      toast.error("Impossible de mettre à jour la validation");
    } finally {
      setToggling(false);
    }
  };

  if (!attachment) return null;

  return (
    <div
      className="fixed inset-0 bg-norea-text/80 z-50 flex items-center justify-center p-4 md:p-8 animate-fade-in"
      onClick={onClose}
      data-testid="pdf-viewer-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full h-full max-w-7xl flex flex-col border border-norea-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-norea-border">
          <div className="w-10 h-10 rounded-md bg-norea-surfaceHover flex items-center justify-center flex-shrink-0 text-norea-accent">
            <FileText className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading text-base font-medium text-norea-text truncate">
                {attachment.normalized_filename || attachment.filename}
              </span>
              {attachment.is_bdc && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-primary text-white rounded">
                  BDC
                </span>
              )}
              {attachment.is_original && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-accent text-white rounded" data-testid="pdf-modal-original-badge">
                  Original
                </span>
              )}
              {attachment.type_piece && (
                <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-norea-surfaceHover text-norea-text border border-norea-border rounded">
                  {attachment.type_piece}
                </span>
              )}
            </div>
            <div className="text-xs text-norea-textMuted mt-0.5">
              Nom d'origine : <span className="font-mono">{attachment.filename}</span>
              {attachment.size ? ` · ${(attachment.size / 1024).toFixed(0)} Ko` : ""}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={toggleValidation}
              disabled={toggling}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition ${
                validated
                  ? "bg-status-completBg border-status-completBorder text-status-completText hover:bg-status-completBg/80"
                  : "bg-white border-norea-border text-norea-text hover:bg-norea-surfaceHover"
              }`}
              data-testid="pdf-modal-validate"
            >
              {validated ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
              {validated ? "Validée" : "Valider"}
            </button>
            <button
              onClick={openInNewTab}
              disabled={!blobUrl}
              className="btn-ghost text-xs"
              title="Ouvrir dans un nouvel onglet"
              data-testid="pdf-modal-newtab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
            <button onClick={downloadCurrent} className="btn-secondary text-xs" data-testid="pdf-modal-download">
              <Download className="w-3.5 h-3.5" />
              Télécharger
            </button>
            <button
              onClick={onClose}
              className="btn-ghost p-2"
              title="Fermer (Échap)"
              data-testid="pdf-modal-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body: a single PDF, NO carousel, NO arrows, NO thumbnail strip */}
        <div className="flex-1 relative bg-norea-bg overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-norea-textMuted">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span className="text-sm">Chargement du PDF…</span>
            </div>
          )}

          {error && !loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-8" data-testid="pdf-modal-error">
              <div className="text-status-erreurText font-medium">{error}</div>
              <div className="text-xs text-norea-textMuted">
                Cette pièce n'est probablement pas archivée (cas des dossiers de démo).
              </div>
            </div>
          )}

          {blobUrl && !loading && !error && (
            <object
              key={blobUrl}
              data={`${blobUrl}#toolbar=1&navpanes=0`}
              type="application/pdf"
              title={attachment.filename}
              className="w-full h-full border-0"
              data-testid="pdf-modal-iframe"
            >
              <iframe
                src={blobUrl}
                title={attachment.filename}
                className="w-full h-full border-0"
              />
            </object>
          )}
        </div>
      </div>
    </div>
  );
}
